// src/components/analytics/MonthCard.js

import React, { useMemo } from "react";
import { CalendarRange, Clock, ListChecks, Award, Smile, List } from "lucide-react";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveBump } from "@nivo/bump";
import { ResponsiveCalendar } from "@nivo/calendar";
import { getPieDetails } from "../../utils/analyticsUtils";
import locales from "../../utils/locales";
import parseDuration from "../../utils/parseDuration";
import { isSameMonth, startOfWeek, addWeeks, isWithinInterval } from "date-fns";

function getWeeklyBumpData(activities, categories, lang, monthDate) {
    if (!activities || !categories || activities.length === 0 || categories.length === 0) return [];
  
    let firstDay = monthDate ? new Date(monthDate) : new Date();
    if (isNaN(firstDay.getTime())) firstDay = new Date();
    firstDay.setDate(1);
  
    const weeks = [];
    let weekStart = startOfWeek(firstDay, { weekStartsOn: 1 });
    let i = 0;
  
    // เดินไปเรื่อยๆ จนสัปดาห์นี้ไม่มีวันไหนอยู่ในเดือนที่สนใจแล้ว หรือครบ 6 สัปดาห์ (safety)
    while (i < 6) {
      // ถ้า weekStart หรือสิ้นสุดสัปดาห์นี้ ยังอยู่ในเดือนเดียวกับ firstDay ให้เพิ่ม
      if (
        isSameMonth(weekStart, firstDay) ||
        isSameMonth(addWeeks(weekStart, 1), firstDay)
      ) {
        weeks.push({
          key: `W${i + 1}`,
          start: new Date(weekStart),
          end: addWeeks(weekStart, 1),
          label: `${lang === "th" ? "สัปดาห์" : "W"}${i + 1}`,
        });
      } else {
        break;
      }
      weekStart = addWeeks(weekStart, 1);
      i++;
    }
  
    const result = categories.map(cat => ({
      id: cat.label[lang],
      data: weeks.map(week => {
        const totalMin = activities.filter(a => {
          const d = new Date(a.activity_date || a.created_at);
          return (
            a.category === cat.value &&
            isWithinInterval(d, { start: week.start, end: new Date(week.end.getTime() - 1) })
          );
        }).reduce((sum, a) => sum + parseDuration(a.duration), 0);
        return { x: week.label, y: +(totalMin / 60).toFixed(2) };
      }),
    })).filter(cat => cat.data.some(point => point.y > 0));
  
    return result;
  }

// --- Helper: Month summary ---
function summarizeMonth(activities, categories, lang) {
  if (!activities || activities.length === 0) return null;
  const totalMin = activities.reduce((sum, a) => sum + parseDuration(a.duration), 0);
  const totalHour = totalMin / 60;

  // Top category
  const catCount = {};
  activities.forEach(a => { catCount[a.category] = (catCount[a.category] || 0) + parseDuration(a.duration || 0); });
  const topCatVal = Object.entries(catCount).sort((a, b) => b[1] - a[1])[0]?.[0];
  const topCategory = categories.find(c => c.value === topCatVal)?.label?.[lang] || "-";

  // Top 3 activities
  const actCount = {};
  activities.forEach(a => { actCount[a.activity] = (actCount[a.activity] || 0) + parseDuration(a.duration || 0); });
  const topActs = Object.entries(actCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([name]) => name);

  // Avg mood
  const moodToScore = { "😊": 5, "😌": 4, "😐": 3, "😔": 2, "😠": 1 };
  const scoreToMood = [null, "😠", "😔", "😐", "😌", "😊"];
  const moodArr = activities.map(a => moodToScore[a.mood]).filter(Boolean);
  const avgMood = moodArr.length > 0 ? scoreToMood[Math.round(moodArr.reduce((a, b) => a + b, 0) / moodArr.length)] : "-";

  const activityCount = activities.length;

  return {
    totalHour,
    topCategory,
    topActs,
    avgMood,
    activityCount,
  };
}

// --- Helper: AreaBump from real data, 6 months (top 4 cat) ---
//function getBumpData(activities, categories, lang, targetMonth) {
//  // 6 months: prev 5 + this month
//  const months = [];
//  let m = targetMonth ? new Date(targetMonth) : new Date();
//  m.setDate(1);
//  for (let i = 5; i >= 0; i--) {
//    const d = subMonths(m, i);
//    months.push({
//      key: format(d, "yyyy-MM"),
//      label: format(d, lang === "th" ? "LLL" : "MMM"),
//      date: d,
//    });
//  }
//  // For each category, sum hours by month
//  const dataPerCat = categories.map(cat => ({
//    id: cat.label[lang],
//    data: months.map(mo => {
//      const totalMin = activities
//        .filter(a => {
//          let adate = (a.activity_date || a.created_at || "").slice(0, 10);
//          if (!adate || !a.category) return false;
//          return a.category === cat.value && isSameMonth(parseISO(adate), mo.date);
//        })
//        .reduce((sum, a) => sum + parseDuration(a.duration), 0);
//      return { x: mo.label, y: +(totalMin / 60).toFixed(2) };
//    }),
//  }));
//  // only show top 4 categories by max
//  const maxY = dataPerCat.map(d => d.data.reduce((max, i) => Math.max(max, i.y), 0));
//  const ranked = dataPerCat
//    .map((d, idx) => ({ ...d, max: maxY[idx] }))
//    .sort((a, b) => b.max - a.max)
//    .slice(0, 4);
//  return ranked;
//}

export default function MonthCard({
    categories,
    activities,
    lang,
    monthLabel,
    month,          // Date or "yyyy-MM-01"
    allActivities,  // <- array กิจกรรมทั้งหมด!
  }) {
    const t = locales[lang];
    const summary = useMemo(() => summarizeMonth(activities, categories, lang), [activities, categories, lang]);
    const bumpData = useMemo(() => getWeeklyBumpData(activities, categories, lang, month), [activities, categories, lang, month]);
    const pieDetails = useMemo(() => getPieDetails(activities, categories, lang), [activities, categories, lang]);
    const pieData = useMemo(() =>
      categories
        .map(cat => {
          const totalMin = activities.filter(a => a.category === cat.value)
            .reduce((sum, a) => sum + parseDuration(a.duration), 0);
          return totalMin > 0 ? {
            id: cat.label[lang], label: cat.label[lang], value: +(totalMin / 60).toFixed(2),
            color: cat.color,
          } : null;
        }).filter(Boolean), [activities, categories, lang]
    );
  
    // --- Calendar (heatmap) per day in month ---
    const start = month ? new Date(month) : new Date();
    start.setDate(1);
    const end = new Date(start);
    end.setMonth(end.getMonth() + 1);
    end.setDate(0);
  
    const calendarData = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const key = d.toISOString().slice(0, 10);
      const totalMin = activities.filter(a => (a.activity_date || a.created_at || "").slice(0, 10) === key)
        .reduce((sum, a) => sum + parseDuration(a.duration), 0);
      if (totalMin > 0) {
        calendarData.push({ day: key, value: +(totalMin / 60).toFixed(2) });
      }
    }
  
    if (!summary)
      return (
        <div className="bg-white rounded-2xl shadow-xl px-6 py-6 mb-5 min-h-[240px] flex flex-col justify-center items-center text-gray-400">
          {lang === "th" ? "ยังไม่มีกิจกรรมในเดือนนี้" : "No activity this month."}
        </div>
      );
  
    return (
      <div className="bg-white rounded-2xl shadow-xl px-6 py-6 mb-5 flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <CalendarRange className="text-indigo-500" size={22} />
          <span className="font-bold text-lg text-neutral-800">
            {monthLabel || (lang === "th" ? "เดือนนี้" : "This Month")}
          </span>
        </div>
  
        {/* Pie Chart + Pie Details */}
        <div className="mb-6">
          <h4 className="font-semibold mb-2 text-gray-800">{lang === "th" ? "สัดส่วนเวลาต่อหมวด" : "Time by Category"}</h4>
          <div className="w-full h-60">
            <ResponsivePie
              data={pieData}
              margin={{ top: 20, right: 80, bottom: 30, left: 80 }}
              innerRadius={0.5}
              padAngle={1.2}
              cornerRadius={5}
              activeOuterRadiusOffset={8}
              colors={{ datum: "data.color" }}
              borderWidth={1}
              borderColor={{ from: "color", modifiers: [["darker", 0.3]] }}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextColor="#555"
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: "color" }}
              arcLabelsSkipAngle={10}
              arcLabelsTextColor="#fff"
              enableArcLinkLabels={false}
              tooltip={({ datum }) =>
                <div className="px-2 py-1 bg-white text-sm rounded shadow border border-gray-100">
                  <b>{datum.label}</b>: {datum.value} {lang === "th" ? "ชม." : "hr"}
                </div>
              }
            />
          </div>
          {/* Pie Details Table */}
          {pieDetails.length > 0 && (
            <div className="w-full mt-2 text-xs">
              <div className="flex font-bold border-b border-gray-200 pb-1 mb-1">
                <div className="w-1/3">{lang === "th" ? "หมวด" : "Category"}</div>
                <div className="w-1/4 text-right">{lang === "th" ? "เวลารวม" : "Total"}</div>
                <div className="w-1/6 text-right">{lang === "th" ? "ครั้ง" : "Count"}</div>
                <div className="w-1/4 text-right">{lang === "th" ? "เฉลี่ย/ครั้ง" : "Avg/Act"}</div>
              </div>
              {pieDetails.map(row => (
                <div key={row.label} className="flex items-center py-0.5 border-b last:border-0 border-gray-100">
                  <div className="w-1/3 flex items-center">
                    <span className="inline-block w-3 h-3 mr-2 rounded-full" style={{ background: row.color }} />
                    {row.label}
                  </div>
                  <div className="w-1/4 text-right">{row.totalHour} {lang === "th" ? "ชม." : "hr"}</div>
                  <div className="w-1/6 text-right">{row.count}</div>
                  <div className="w-1/4 text-right">{row.avgHour} {lang === "th" ? "ชม." : "hr"}</div>
                </div>
              ))}
            </div>
          )}
        </div>
  
        {/* AreaBump (Category Trends) */}
      <div className="w-full overflow-x-auto mb-6">
        <div className="min-w-[700px] max-w-[1200px] mx-auto aspect-[2.5/1] rounded-xl shadow bg-white">
          <ResponsiveBump
            data={bumpData}
            colors={{ scheme: "set2" }}
            enableLabel={true}
            label={d => d.id}
            labelTextColor={{ from: 'color', modifiers: [['darker', 2]]}}
            margin={{ top: 20, right: 60, bottom: 20, left: 20 }}
            theme={{
                labels: {
                  text: { fontSize: 16, fontWeight: 700, fill: "#333" }
                }
            }}
            lineWidth={4}
            activeLineWidth={6}
            inactiveLineWidth={3}
            inactiveOpacity={0.4}
            pointSize={10}
            activePointSize={16}
            inactivePointSize={0}
            pointBorderWidth={3}
            axisTop={null}
            axisBottom={null}
            axisLeft={null}
            tooltip={({ series, x, y }) =>
              <div className="px-2 py-1 bg-white text-sm rounded shadow border border-gray-100">
                <b>{series.id}</b> {x}: {y}
              </div>
            }
          />
        </div>
      </div>

      {/* Calendar (Heatmap) */}
      <div className="mb-6">
        <h4 className="font-semibold mb-2 text-gray-800">{lang === "th" ? "กิจกรรมตามวัน" : "Activity by Day"}</h4>
        <div className="w-full h-32">
          <ResponsiveCalendar
            data={calendarData}
            from={start.toISOString().slice(0, 10)}
            to={end.toISOString().slice(0, 10)}
            emptyColor="#f3f4f6"
            colors={["#c7d2fe", "#818cf8", "#6366f1", "#312e81"]}
            margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
            yearSpacing={30}
            monthBorderColor="#fff"
            dayBorderWidth={2}
            dayBorderColor="#fff"
            tooltip={({ day, value }) =>
              <div className="px-2 py-1 bg-white text-sm rounded shadow border border-gray-100">
                <b>{day}</b>: {value || 0} {lang === "th" ? "ชม." : "hr"}
              </div>
            }
          />
        </div>
      </div>

      {/* Summary Section */}
      <div className="grid grid-cols-2 gap-y-2 mb-3">
        <div className="flex items-center gap-2">
          <Clock size={18} className="text-blue-400" />
          <span>{t.total_time}:</span>
          <span className="font-semibold text-blue-700">
            {Number(summary.totalHour).toFixed(1)} {lang === "th" ? "ชม." : "hr"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <ListChecks size={18} className="text-pink-400" />
          <span>{t.top_category}:</span>
          <span className="font-semibold text-pink-600">{summary.topCategory || "-"}</span>
        </div>
        {/* Top 3 activities */}
        <div className="flex items-center gap-2 col-span-2">
          <Award size={18} className="text-green-400" />
          <span className="font-medium text-gray-600">
            {lang === "th" ? "Top 3 กิจกรรม:" : "Top 3 activities:"}
          </span>
          <div className="flex flex-wrap gap-1">
            {summary.topActs.length > 0
              ? summary.topActs.map((a, idx) => (
                <span
                  key={idx}
                  className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium border border-green-100"
                >
                  {a}
                </span>
              ))
              : <span>-</span>
            }
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Smile size={18} className="text-yellow-400" />
          <span>{t.mood || "Mood"}:</span>
          <span className="font-semibold text-yellow-600">{summary.avgMood || "-"}</span>
        </div>
        <div className="flex items-center gap-2">
          <List size={18} className="text-cyan-400" />
          <span>{t.activities_count || "Activities"}:</span>
          <span className="font-semibold text-cyan-700">{summary.activityCount || "-"}</span>
        </div>
      </div>
    </div>);
}